import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import {
  CATEGORYPRODUCT,
  ICategoryProductPagingRes,
} from '@/shared/interfaces/erp/inventario';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchCategoryProduct = ({
  page = 1,
  page_size = 10,
  nombre = '',
}: GetCategoryProductParams) => {
  return useQuery({
    queryKey: ['categoria-producto', page, nombre, page_size],
    queryFn: async () => {
      return getCategoryProducts({ page, nombre, page_size });
    },
  });
};

export const useFetchCategoryProductEnabled = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetCategoryProductParams>) => {
  return useQuery({
    queryKey: ['categoria-producto', params],
    queryFn: async () => {
      return getCategoryProducts(params);
    },
    enabled,
  });
};

export const useGetCategoryProduct = (id: string) => {
  return useQuery({
    queryKey: ['categoria-productos', id],
    queryFn: async () => {
      return getCategoryProduct(id);
    },
  });
};

export const useCreateCategoryProduct = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createCategoryProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoria-producto'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Categoria de producto creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateCategoryProduct = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCategoryProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoria-producto'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ??
            'Categoria de producto actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export interface GetCategoryProductParams extends Partial<CATEGORYPRODUCT> {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreateCategoryProductParams = Omit<
  CATEGORYPRODUCT,
  'id_categoriaproducto'
>;
export type UpdateCategoryProductParams = {
  id: string;
  data: CreateCategoryProductParams;
};

export const getCategoryProducts = async (
  params?: GetCategoryProductParams
) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<ICategoryProductPagingRes>(
    `${urlBackend}api/v1/categoria-producto/?${queryParams}`,
    true
  );
};

export const getCategoryProduct = async (id: string) => {
  try {
    return await get<CATEGORYPRODUCT>(
      `${urlBackend}api/v1/categoria-producto/${id}/`,
      true
    );
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createCategoryProduct = async (
  data: CreateCategoryProductParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<CATEGORYPRODUCT>(
    `${urlBackend}api/v1/categoria-producto/`,
    data,
    true
  );
};
export const updateCategoryProduct = async ({
  id,
  data,
}: UpdateCategoryProductParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<CATEGORYPRODUCT>(
    `${urlBackend}api/v1/categoria-producto/${id}/`,
    data,
    true
  );
};
